import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

export default function Testimonials() {
  const { t, dir } = useI18n();
  const isRtl = dir === "rtl";
  const list = t.testimonials.items;
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (paused) return undefined;
    timer.current = setInterval(() => {
      setIdx((p) => (p + 1) % list.length);
    }, 5500);
    return () => clearInterval(timer.current);
  }, [paused, list.length]);

  const go = (next) => {
    setIdx((p) => (p + (next ? 1 : -1) + list.length) % list.length);
  };

  return (
    <section
      id="testimonials"
      className="relative bg-secondary text-secondary-foreground overflow-hidden"
      data-testid="testimonials-section"
    >
      <div className="pointer-events-none absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full bg-primary/15 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -right-32 w-[420px] h-[420px] rounded-full bg-primary/15 blur-3xl" />

      <div className="container-page section-y relative">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7"
          >
            <span className="overline">{t.testimonials.eyebrow}</span>
            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-black leading-[1.05]">
              {t.testimonials.heading1} <br />
              <span className="text-primary">{t.testimonials.heading1Accent}</span>
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-5 text-secondary-foreground/70 leading-relaxed"
          >
            {t.testimonials.description}
          </motion.p>
        </div>

        <div
          className="relative bg-background/5 border border-white/10 p-8 md:p-14 min-h-[300px]"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          data-testid="testimonials-stage"
        >
          <Quote className="absolute top-6 start-6 h-10 w-10 text-primary/40" />

          <AnimatePresence mode="wait">
            <motion.blockquote
              key={idx}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -14 }}
              transition={{ duration: 0.45 }}
              className="relative max-w-3xl mx-auto text-center pt-6"
              data-testid={`testimonial-${idx}`}
            >
              <p className="text-lg md:text-2xl lg:text-3xl font-medium leading-snug text-secondary-foreground">
                &ldquo;{list[idx].quote}&rdquo;
              </p>
              <div className="mt-8 h-px w-12 bg-primary mx-auto" />
              <div className="mt-6">
                <div className="text-sm md:text-base font-bold">{list[idx].name}</div>
                <div className="mt-1 text-xs uppercase tracking-[0.18em] text-primary">
                  {list[idx].company}
                </div>
              </div>
            </motion.blockquote>
          </AnimatePresence>

          {/* Controls */}
          <div className="mt-10 flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => go(false)}
              className="h-10 w-10 grid place-items-center border border-white/15 hover:border-primary hover:text-primary transition-colors"
              aria-label="Previous"
              data-testid="testimonial-prev"
            >
              {isRtl ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
            </button>
            <div className="flex items-center gap-2 px-2">
              {list.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setIdx(i)}
                  className={`h-1.5 transition-all duration-300 ${
                    i === idx ? "w-10 bg-primary" : "w-3 bg-white/25 hover:bg-white/40"
                  }`}
                  aria-label={`Go to testimonial ${i + 1}`}
                  data-testid={`testimonial-dot-${i}`}
                />
              ))}
            </div>
            <button
              type="button"
              onClick={() => go(true)}
              className="h-10 w-10 grid place-items-center border border-white/15 hover:border-primary hover:text-primary transition-colors"
              aria-label="Next"
              data-testid="testimonial-next"
            >
              {isRtl ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
